import { useParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { useProjectStore } from 'Store/Project'
import { shallow } from 'zustand/shallow'
import * as Yup from 'yup'

export interface Props {
  isOpen: boolean
  onClose: () => void
  onSave: () => void
}

const INITIAL_FORM_STATE = {
  recordedAt: new Date(),
  note: '',
  noteBy: '',
  attachments: [] as File[],
}

const ViewModel = (props: Props) => {
  const validationSchema = Yup.object().shape({
    recordedAt: Yup.date().required('กรุณาเลือกวันที่บันทึก'),
    note: Yup.string().required('กรุณากรอกบันทึกข้อความ'),
  })

  const { isOpen, onClose, onSave } = props
  const { id } = useParams<{ id: string }>()
  const { getProject, addNote } = useProjectStore(
    state => ({
      getProject: state.getOne,
      addNote: state.addNote,
    }),
    shallow
  )

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formState, setFormState] = useState(INITIAL_FORM_STATE)
  const [formError, setFormError] = useState<{ [key: string]: string }>({})

  const onChangeFormState = (field: string, value: any) => {
    setFormState(prevState => ({
      ...prevState,
      [field]: value,
    }))
  }

  const onAddFileAttachment = (files: FileList | null) => {
    if (files && files.length > 0) {
      const newFiles = Array.from(files)
      setFormState(prevState => ({
        ...prevState,
        attachments: [...prevState.attachments, ...newFiles],
      }))
    }
  }

  const onRemoveFileAttachment = (index: number) => {
    setFormState(prevState => ({
      ...prevState,
      attachments: prevState.attachments.filter((_, idx) => idx !== index),
    }))
  }

  const resetFormState = () => {
    setFormState(INITIAL_FORM_STATE)
    setFormError({})
  }

  const onSubmit = () => {
    validationSchema
      .validate(formState, { abortEarly: false })
      .then(async () => {
        setIsSubmitting(true)
        setFormError({})

        const formData = new FormData()
        formData.append('recordedAt', formState.recordedAt.toISOString())
        formData.append('note', formState.note)
        formData.append('noteBy', formState.noteBy)
        formState.attachments.forEach((file, index) => {
          formData.append(`attachments[]`, file)
        })

        const { success } = await addNote(parseInt(id ?? ''), formData)
        if (success) {
          getProject(parseInt(id ?? ''))
          resetFormState()
          onSave()
        }

        setIsSubmitting(false)
      })
      .catch((err: Yup.ValidationError) => {
        const errors: { [key: string]: string } = {}
        err.inner.forEach(error => {
          if (error.path) {
            errors[error.path] = error.message
          }
        })
        setFormError(errors)
      })
  }

  useEffect(() => {
    if (!isOpen) {
      resetFormState()
    }
  }, [isOpen])

  return {
    isSubmitting,
    isOpen,
    formState,
    formError,
    onChangeFormState,
    onAddFileAttachment,
    onRemoveFileAttachment,
    onClose,
    onSubmit,
  }
}

export default ViewModel
